// modules/Battle/BattleRewardWindow.js
// 战斗奖励显示窗口 - 美观的奖励弹窗

export default class BattleRewardWindow {
    constructor(scene) {
        this.scene = scene;
        this.elements = {
            container: null,
            background: null,
            border: null,
            title: null,
            content: null,
            scrollTimer: null
        };
        this.currentScrollIndex = 0;
        this.scrollLines = [];
        this.isVisible = false;
        this.keyHandler = null;
        this.onCompleteCallback = null;
        this.scrollCompleted = false;
        this.keyboardKeys = null;
        this.autoCloseTimer = null;
        this.contentScrollY = 0;
        this.maxScrollY = 0;
        this.contentHeight = 0;
        this.contentAreaY = 0; // 保存内容区域的初始Y位置
    }
    
    /**
     * 创建奖励窗口
     */
    create() {
        const { width, height } = this.scene.cameras.main;
        
        // 创建容器（用于统一管理所有元素）
        this.elements.container = this.scene.add.container(width / 2, height / 2);
        this.elements.container.setScrollFactor(0);
        this.elements.container.setDepth(10000);
        this.elements.container.setVisible(false);
        this.elements.container.setAlpha(0);
        
        // 窗口尺寸
        const windowWidth = 600;
        const windowHeight = 500;
        
        // 创建半透明遮罩层（让背景变暗）
        const mask = this.scene.add.graphics();
        mask.fillStyle(0x000000, 0.7);
        mask.fillRect(0, 0, width, height);
        mask.setScrollFactor(0);
        mask.setDepth(9999);
        mask.setAlpha(0);
        this.elements.mask = mask;
        
        // 创建主背景（渐变效果）
        this.elements.background = this.scene.add.graphics();
        
        // 外发光效果（多层边框模拟发光）
        for (let i = 0; i < 3; i++) {
            const glowAlpha = 0.3 - i * 0.1;
            const glowSize = windowWidth + i * 10;
            const glowHeight = windowHeight + i * 10;
            this.elements.background.lineStyle(2, 0xffd700, glowAlpha); // 金色发光
            this.elements.background.strokeRoundedRect(
                -glowSize / 2, -glowHeight / 2,
                glowSize, glowHeight,
                15
            );
        }
        
        // 主背景（深色半透明）
        this.elements.background.fillStyle(0x1a1a1a, 0.95);
        this.elements.background.fillRoundedRect(
            -windowWidth / 2, -windowHeight / 2,
            windowWidth, windowHeight,
            12
        );
        
        // 金色边框
        this.elements.background.lineStyle(3, 0xffd700, 1); // 金色
        this.elements.background.strokeRoundedRect(
            -windowWidth / 2, -windowHeight / 2,
            windowWidth, windowHeight,
            12
        );
        
        // 内边框（装饰）
        this.elements.background.lineStyle(1, 0x884400, 0.6); // 深金色
        this.elements.background.strokeRoundedRect(
            -windowWidth / 2 + 5, -windowHeight / 2 + 5,
            windowWidth - 10, windowHeight - 10,
            8
        );
        
        this.elements.background.setScrollFactor(0);
        
        // 创建标题区域背景
        const titleBg = this.scene.add.graphics();
        titleBg.fillStyle(0x2a1a00, 0.8); // 深棕色半透明
        titleBg.fillRoundedRect(
            -windowWidth / 2 + 10, -windowHeight / 2 + 10,
            windowWidth - 20, 60,
            8
        );
        
        // 标题区域边框
        titleBg.lineStyle(2, 0xff9900, 0.8); // 橙色边框
        titleBg.strokeRoundedRect(
            -windowWidth / 2 + 10, -windowHeight / 2 + 10,
            windowWidth - 20, 60,
            8
        );
        
        titleBg.setScrollFactor(0);
        this.elements.titleBg = titleBg;
        
        // 创建标题文本
        this.elements.title = this.scene.add.text(
            0, -windowHeight / 2 + 40,
            '战斗胜利！',
            {
                fontSize: '32px',
                fill: '#ffd700',
                fontFamily: 'Arial',
                fontWeight: 'bold',
                stroke: '#000000',
                strokeThickness: 4,
                shadow: {
                    offsetX: 2,
                    offsetY: 2,
                    color: '#000000',
                    blur: 5,
                    fill: true
                }
            }
        );
        this.elements.title.setOrigin(0.5);
        this.elements.title.setScrollFactor(0);
        
        // 内容区域尺寸
        // 标题区域从 -windowHeight/2 + 10 开始，高度约60，所以内容从 -windowHeight/2 + 80 开始
        const contentAreaX = -windowWidth / 2 + 30;
        this.contentAreaY = -windowHeight / 2 + 80; // 保存内容区域的初始Y位置
        const contentAreaWidth = windowWidth - 60;
        const contentAreaHeight = windowHeight - 130; // 调整高度，留出标题和底部提示的空间
        
        // 创建内容容器（用于滚动）
        this.elements.contentContainer = this.scene.add.container(
            contentAreaX,
            this.contentAreaY
        );
        this.elements.contentContainer.setScrollFactor(0);
        
        // 创建内容遮罩图形（限制显示区域）
        // 遮罩作为场景中的独立对象，但位置要匹配窗口容器坐标系
        // 由于窗口容器在(width/2, height/2)，遮罩的屏幕位置需要加上这个偏移
        const screenWidth = this.scene.cameras.main.width;
        const screenHeight = this.scene.cameras.main.height;
        this.elements.contentMask = this.scene.add.graphics();
        this.elements.contentMask.fillStyle(0xffffff, 1);
        // 遮罩的世界坐标 = 窗口中心 + 相对位置
        this.elements.contentMask.fillRect(
            screenWidth / 2 + contentAreaX,
            screenHeight / 2 + this.contentAreaY,
            contentAreaWidth,
            contentAreaHeight
        );
        this.elements.contentMask.setScrollFactor(0);
        this.elements.contentMask.setVisible(false); // 遮罩本身不可见
        this.elements.contentMask.setDepth(10001); // 确保在正确层级
        
        // 创建内容文本（放在容器中）
        this.elements.content = this.scene.add.text(
            0,
            0,
            '',
            {
                fontSize: '18px',
                fill: '#ffffff',
                fontFamily: 'Arial',
                wordWrap: { width: contentAreaWidth },
                lineSpacing: 8,
                shadow: {
                    offsetX: 1,
                    offsetY: 1,
                    color: '#000000',
                    blur: 3,
                    fill: true
                }
            }
        );
        this.elements.content.setOrigin(0, 0);
        
        // 将文本添加到容器
        this.elements.contentContainer.add(this.elements.content);
        
        // 应用遮罩到容器
        const contentMask = this.elements.contentMask.createGeometryMask();
        this.elements.contentContainer.setMask(contentMask);
        
        // 添加提示文本（在底部）
        const hintText = this.scene.add.text(
            0, windowHeight / 2 - 30,
            '按空格键或点击关闭 | 上下键或滚轮滚动',
            {
                fontSize: '14px',
                fill: '#aaaaaa',
                fontFamily: 'Arial',
                fontStyle: 'italic'
            }
        );
        hintText.setOrigin(0.5);
        hintText.setScrollFactor(0);
        this.elements.hintText = hintText;
        
        // 将所有元素添加到容器（遮罩不添加到容器，只在需要时创建几何遮罩）
        this.elements.container.add([
            this.elements.background,
            this.elements.titleBg,
            this.elements.title,
            this.elements.contentContainer,
            this.elements.hintText
        ]);
    }
    
    /**
     * 显示奖励窗口并逐行滚动显示奖励信息
     * @param {string} rewardMessage - 奖励消息文本（多行，用\n分隔）
     * @param {number} delayPerLine - 每行显示的延迟（毫秒），默认400
     * @param {Function} onComplete - 完成回调
     */
    showRewards(rewardMessage, delayPerLine = 400, onComplete = null) {
        if (this.isVisible) {
            return;
        }
        
        // 清除之前的滚动和事件监听
        this.clearScroll();
        this.removeKeyboardListener();
        
        // 保存完成回调
        this.onCompleteCallback = onComplete;
        this.scrollCompleted = false;
        
        // 按行分割消息，并过滤掉空行
        const lines = rewardMessage.split('\n').filter(line => line.trim() !== '');
        if (lines.length === 0) {
            if (onComplete) onComplete();
            return;
        }
        
        this.scrollLines = lines;
        this.currentScrollIndex = 0;
        this.contentScrollY = 0;
        this.maxScrollY = 0;
        this.contentHeight = 0;
        
        // 清空内容并重置滚动位置
        this.elements.content.setText('');
        this.elements.contentContainer.y = this.contentAreaY;
        
        // 显示窗口（淡入动画）
        this.elements.mask.setVisible(true);
        this.elements.container.setVisible(true);
        this.isVisible = true;
        
        // 设置遮罩可点击（点击关闭）- 只有在滚动完成后才能点击关闭
        const { width, height } = this.scene.cameras.main;
        if (!this.elements.mask.input) {
            this.elements.mask.setInteractive(new Phaser.Geom.Rectangle(0, 0, width, height), Phaser.Geom.Rectangle.Contains);
        }
        // 每次显示时重新设置点击事件
        this.elements.mask.removeAllListeners('pointerdown');
        this.elements.mask.on('pointerdown', () => {
            if (this.isVisible && this.scrollCompleted) {
                this.handleClose();
            }
        });
        
        // 遮罩淡入
        this.scene.tweens.add({
            targets: this.elements.mask,
            alpha: 1,
            duration: 300,
            ease: 'Power2'
        });
        
        // 窗口淡入并缩放
        this.elements.container.setScale(0.8);
        this.scene.tweens.add({
            targets: this.elements.container,
            alpha: 1,
            scale: 1,
            duration: 400,
            ease: 'Back.easeOut'
        });
        
        // 提示文本闪烁动画
        this.scene.tweens.add({
            targets: this.elements.hintText,
            alpha: { from: 0.3, to: 1 },
            duration: 1000,
            repeat: -1,
            yoyo: true,
            ease: 'Sine.easeInOut'
        });
        
        // 逐行显示奖励
        const showNextLine = () => {
            if (this.currentScrollIndex >= this.scrollLines.length) {
                // 所有行显示完毕
                this.scrollCompleted = true;
                // 设置键盘监听（等待用户按键关闭）
                this.setupKeyboardListener();
                return;
            }
            
            const currentText = this.elements.content.text;
            const lineToAdd = this.scrollLines[this.currentScrollIndex];
            
            // 添加新行
            const newText = currentText === '' 
                ? lineToAdd
                : currentText + '\n' + lineToAdd;
            
            // 设置新文本
            this.elements.content.setText(newText);
            
            // 更新内容高度和最大滚动位置
            this.contentHeight = this.elements.content.height;
            const maxContentHeight = 500 - 140; // windowHeight - 标题和底部空间
            this.maxScrollY = Math.max(0, this.contentHeight - maxContentHeight);
            
            // 如果内容超出，自动滚动到底部
            if (this.contentHeight > maxContentHeight) {
                this.contentScrollY = -this.maxScrollY;
                this.elements.contentContainer.y = this.contentAreaY + this.contentScrollY;
            } else {
                // 重置滚动位置
                this.contentScrollY = 0;
                this.elements.contentContainer.y = this.contentAreaY;
            }
            
            // 淡入动画效果（如果行不为空）
            if (lineToAdd.trim() !== '') {
                this.elements.content.setAlpha(0.5);
                this.scene.tweens.add({
                    targets: this.elements.content,
                    alpha: 1,
                    duration: 200,
                    ease: 'Power1'
                });
            }
            
            this.currentScrollIndex++;
            
            // 继续显示下一行
            if (this.currentScrollIndex < this.scrollLines.length) {
                this.elements.scrollTimer = this.scene.time.delayedCall(delayPerLine, showNextLine);
            } else {
                // 所有行显示完毕
                this.elements.content.setAlpha(1);
                this.scrollCompleted = true;
                // 设置键盘监听（等待用户按键关闭）
                this.setupKeyboardListener();
            }
        };
        
        // 延迟一下再开始显示，让窗口动画完成
        this.scene.time.delayedCall(500, () => {
            showNextLine();
        });
    }
    
    /**
     * 设置键盘监听（用于关闭窗口和滚动）
     */
    setupKeyboardListener() {
        // 移除之前的监听
        this.removeKeyboardListener();
        
        if (this.scene.input && this.scene.input.keyboard) {
            // 空格键 - 关闭窗口
            const spaceKey = this.scene.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.SPACE);
            
            // 先移除可能存在的旧监听
            if (spaceKey.removeAllListeners) {
                spaceKey.removeAllListeners('down');
            }
            
            this.keyHandler = () => {
                if (this.isVisible && this.scrollCompleted) {
                    this.handleClose();
                }
            };
            
            spaceKey.on('down', this.keyHandler);
            this.keyboardKeys = [spaceKey];
            
            // 上下箭头键 - 滚动内容
            const upKey = this.scene.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.UP);
            const downKey = this.scene.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.DOWN);
            
            upKey.on('down', () => {
                if (this.isVisible) {
                    this.scrollContent(-50);
                }
            });
            
            downKey.on('down', () => {
                if (this.isVisible) {
                    this.scrollContent(50);
                }
            });
            
            this.keyboardKeys.push(upKey, downKey);
            
            // 鼠标滚轮滚动
            if (this.scene.input && this.scene.input.mouseWheel) {
                this.scene.input.on('wheel', (pointer, gameObjects, deltaX, deltaY, deltaZ) => {
                    if (this.isVisible) {
                        this.scrollContent(deltaY);
                    }
                });
            }
        }
        
        // 自动关闭（8秒后）
        this.autoCloseTimer = this.scene.time.delayedCall(8000, () => {
            if (this.isVisible && this.scrollCompleted) {
                this.handleClose();
            }
        });
    }
    
    /**
     * 滚动内容
     * @param {number} delta - 滚动增量（正数向下，负数向上）
     */
    scrollContent(delta) {
        if (!this.elements.contentContainer || !this.elements.content) {
            return;
        }
        
        // 计算新的滚动位置
        const newScrollY = this.contentScrollY - delta;
        
        // 限制滚动范围（内容区域高度为 500 - 140 = 360）
        const minScrollY = 0;
        const maxContentAreaHeight = 360;
        const maxScrollY = Math.max(0, this.contentHeight - maxContentAreaHeight);
        
        this.contentScrollY = Phaser.Math.Clamp(newScrollY, -maxScrollY, minScrollY);
        
        // 更新容器位置
        this.elements.contentContainer.y = this.contentAreaY + this.contentScrollY;
    }
    
    /**
     * 移除键盘监听
     */
    removeKeyboardListener() {
        // 移除按键监听
        if (this.keyboardKeys) {
            this.keyboardKeys.forEach(key => {
                if (key && key.removeAllListeners) {
                    key.removeAllListeners();
                }
                if (key && key.destroy) {
                    key.destroy();
                }
            });
            this.keyboardKeys = [];
        }
        
        this.keyHandler = null;
        
        // 清除自动关闭定时器
        if (this.autoCloseTimer) {
            this.autoCloseTimer.destroy();
            this.autoCloseTimer = null;
        }
    }
    
    /**
     * 处理关闭窗口
     */
    handleClose() {
        if (!this.isVisible) {
            return;
        }
        
        this.removeKeyboardListener();
        this.scrollCompleted = false;
        
        // 隐藏窗口
        this.hide(() => {
            if (this.onCompleteCallback) {
                this.onCompleteCallback();
                this.onCompleteCallback = null;
            }
        });
    }
    
    /**
     * 隐藏奖励窗口
     * @param {Function} onComplete - 完成回调
     */
    hide(onComplete = null) {
        if (!this.isVisible) {
            if (onComplete) onComplete();
            return;
        }
        
        // 检查元素是否存在
        if (!this.elements.mask || !this.elements.container) {
            this.isVisible = false;
            if (onComplete) onComplete();
            return;
        }
        
        this.clearScroll();
        
        // 禁用交互（防止重复点击）
        this.isVisible = false;
        
        // 淡出动画
        this.scene.tweens.add({
            targets: [this.elements.mask, this.elements.container],
            alpha: 0,
            duration: 300,
            ease: 'Power2',
            onComplete: () => {
                if (this.elements.mask) {
                    this.elements.mask.setVisible(false);
                    this.elements.mask.setAlpha(0);
                }
                if (this.elements.container) {
                    this.elements.container.setVisible(false);
                    this.elements.container.setAlpha(0);
                }
                if (onComplete) {
                    onComplete();
                }
            }
        });
        
        // 缩放动画
        if (this.elements.container) {
            this.scene.tweens.add({
                targets: this.elements.container,
                scale: 0.8,
                duration: 300,
                ease: 'Power2'
            });
        }
    }
    
    /**
     * 清除滚动显示
     */
    clearScroll() {
        if (this.elements.scrollTimer) {
            this.elements.scrollTimer.destroy();
            this.elements.scrollTimer = null;
        }
        this.scrollLines = [];
        this.currentScrollIndex = 0;
        this.scrollCompleted = false;
        this.contentScrollY = 0;
        this.maxScrollY = 0;
        this.contentHeight = 0;
    }
    
    /**
     * 销毁奖励窗口
     */
    destroy() {
        this.clearScroll();
        this.removeKeyboardListener();
        
        if (this.elements.mask) {
            this.elements.mask.destroy();
        }
        
        if (this.elements.container) {
            this.elements.container.destroy(true);
        }
        
        this.elements = {
            container: null,
            background: null,
            border: null,
            title: null,
            content: null,
            scrollTimer: null
        };
        
        this.onCompleteCallback = null;
    }
}

